// src/pages/providers/2_dashboard/ClinicianDashboard.js
//
// Main clinician dashboard with quick-filter chips for triaging patients.
//
// Features:
//   - Quick-filter chip toolbar above the Current Patients table
//   - Chips disable the Search & Filter popover when active (mutually exclusive)
//   - Activating a chip clears any active text/select/date filters
//   - Chip state resets when switching between Current / Archived tabs
//   - Org-aware chip visibility (e.g. "Needs clinician assigned" is hidden for
//     orgs that require clinician assignment during patient setup)
//   - Search & Filter includes an org-wide "Assigned to" single-select of
//     assignable clinicians / clinical Org. Admins. Clinician users still only
//     see patients they are already assigned to (data load + rules), so picking
//     a colleague shows the shared caseload.

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import Layout from '../../../components/Layout';
import PatientTable from './components/PatientTable';
import ArchivedPatientsTable from './components/ArchivedPatientsTable';
import QuickFilterChips from './components/QuickFilterChips';
import { GlobalStateContext } from '../../../context/GlobalStateContext';
import StatusMessage from '../../../components/StatusMessage';
import PatientSetupForm from './components/PatientSetupForm';
import * as Tabs from '@radix-ui/react-tabs';
import { SearchIcon } from '@heroicons/react/solid';
import * as Popover from '@radix-ui/react-popover';
import { auth, db, functions } from '../../../firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SignatureDialog from '../../../components/SignatureDialog';
import { getVisibleChipKeys } from '../../../utils/patientFilterUtils';
import { buildAssignableClinicianSelectOptions } from '../../../utils/assignedClinicianFilter';
import useSessionState from '../../../hooks/useSessionState';
import { DASHBOARD_KEYS } from '../../../utils/dashboardViewState';
import { assessmentTypeSelectOptions, orgHasContractTypes } from '../../../constants/assessmentTypesByOrg';
import substringFilterOption from './utils/substringFilterOption';
import useRecentlyViewedPatients from '../../../hooks/useRecentlyViewedPatients';
import RecentlyViewedDropdown from './components/RecentlyViewedDropdown';

// Default filter state used to clear search/filter values when a chip is
// activated, and as the initial state on mount.
const DEFAULT_CURRENT_FILTERS = {
  patientName: '',
  emailAddress: '',
  referenceNumber: '',
  paymentMethod: null,
  assessmentType: null,
  assessmentModule: null,
  assignedTo: null,
  dateRange: {
    startDate: null,
    endDate: null
  }
};

// Default filter state for the Archived tab. Extracted to a constant so it can
// seed the session-persisted state cleanly (mirrors DEFAULT_CURRENT_FILTERS).
const DEFAULT_ARCHIVED_FILTERS = {
  patientName: '',
  emailAddress: '',
  referenceNumber: '',
  paymentMethod: null,
  assessmentType: null,
  assessmentModule: null,
  assignedTo: null,
  archiveReason: null,
  dateRange: {
    startDate: null,
    endDate: null
  },
  archiveDateRange: {
    startDate: null,
    endDate: null
  }
};

// Options for the pathway filter dropdown.
const ASSESSMENT_MODULE_OPTIONS = [
  { value: 'adultAdhd', label: 'Adult ADHD' },
  { value: 'adultAutism', label: 'Adult Autism' },
  { value: 'cypAdhd', label: 'CYP ADHD' },
];

function FilterPopoverContent({ onApplyFilters, initialValues, isOpen, onOpenChange, isArchived = false, disabled = false, showModuleFilter = false, assignedToOptions = [] }) {
  const { userData } = useContext(GlobalStateContext);
  
  // Local state for form inputs
  const [localFormInputs, setLocalFormInputs] = useState({
    ...initialValues,
    dateRange: {
      startDate: initialValues.dateRange?.startDate || null,
      endDate: initialValues.dateRange?.endDate || null
    },
    ...(isArchived && {
      archiveDateRange: {
        startDate: initialValues.archiveDateRange?.startDate || null,
        endDate: initialValues.archiveDateRange?.endDate || null
      }
    })
  });

  const handleLocalInputChange = (field, value) => {
    setLocalFormInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Re-seed the form from the current filters each time the popover opens. A
  // filter cleared via its badge updates the parent only, so without this the
  // form would keep the stale typed value (e.g. a removed name still showing).
  // Keyed on `isOpen` only — re-seeding on initialValues identity changes could
  // wipe in-progress edits, and the parent filters only change on Apply/remove.
  useEffect(() => {
    if (!isOpen) return;
    setLocalFormInputs({
      ...initialValues,
      dateRange: {
        startDate: initialValues.dateRange?.startDate || null,
        endDate: initialValues.dateRange?.endDate || null
      },
      ...(isArchived && {
        archiveDateRange: {
          startDate: initialValues.archiveDateRange?.startDate || null,
          endDate: initialValues.archiveDateRange?.endDate || null
        }
      })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const paymentMethodOptions = [
    { value: 'Bupa', label: 'Bupa' },
    { value: 'Self-funded', label: 'Self-funded' }
  ];

  // Contract Type filter options — single source of truth in
  // src/constants/assessmentTypesByOrg.js (includes NHS - Kent for WLC).
  const assessmentTypeOptions = assessmentTypeSelectOptions(userData?.organisation);

  const archiveReasonOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'not_proceeding', label: 'Not Proceeding' }
  ];

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
    <Popover.Trigger asChild>
      <button
        className={`flex flex-row items-center justify-center p-2 rounded-xl transition-colors duration-200 ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-100'
        }`}
        aria-label="Open filter panel"
        aria-disabled={disabled}
        disabled={disabled}
      >
        <SearchIcon className="w-5 h-5 mr-1 text-gray-600" />
        <span className="text-gray-600 text-xs font-medium">Search & Filter</span>
      </button>
    </Popover.Trigger>

    <Popover.Portal>
        <Popover.Content
          className="text-xs bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-[300px] animate-in fade-in zoom-in-95 duration-200"
          sideOffset={5}
        >

    <div className="flex flex-col gap-4">
      <div>
        <label className="block font-medium text-gray-700 mb-1">
          Patient name contains:
        </label>
        <input
          type="text"
          value={localFormInputs.patientName}
          onChange={(e) => handleLocalInputChange('patientName', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          placeholder="Enter patient name..."
        />
      </div>

      <div>
        <label className="block font-medium text-gray-700 mb-1">
          Email address contains:
        </label>
        <input
          type="text"
          value={localFormInputs.emailAddress}
          onChange={(e) => handleLocalInputChange('emailAddress', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          placeholder="Enter email..."
        />
      </div>

      <div>
        <label className="block font-medium text-gray-700 mb-1">
          Reference number contains:
        </label>
        <input
          type="text"
          value={localFormInputs.referenceNumber}
          onChange={(e) => handleLocalInputChange('referenceNumber', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          placeholder="Enter reference number..."
        />
      </div>

      <div>
        <label className="block font-medium text-gray-700 mb-1">
          Assigned to:
        </label>
        <Select
          value={localFormInputs.assignedTo}
          onChange={(option) => handleLocalInputChange('assignedTo', option)}
          options={assignedToOptions}
          isClearable
          isSearchable
          filterOption={substringFilterOption}
          placeholder="Select a clinician..."
          className="w-full"
          classNamePrefix="select"
        />
      </div>

      <div>
            <label className="block font-medium text-gray-700 mb-1">
              Date added:
            </label>
            <div className="flex items-center gap-2">
              <DatePicker
                selectsRange={true}
                startDate={localFormInputs.dateRange.startDate}
                endDate={localFormInputs.dateRange.endDate}
                onChange={(update) => {
                  const [start, end] = update;
                  setLocalFormInputs(prev => ({
                    ...prev,
                    dateRange: {
                      startDate: start,
                      endDate: end
                    }
                  }));
                }}
                isClearable={true}
                placeholderText="Select date range..."
                className="w-[266px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                dateFormat="dd MMM yyyy"
              />
            </div>
          </div>

          {userData?.organisation === "Phoenix Mental Health Services" && (
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Payment method:
              </label>
              <Select
                value={localFormInputs.paymentMethod}
                onChange={(option) => handleLocalInputChange('paymentMethod', option)}
                options={paymentMethodOptions}
                isClearable
                className="w-full"
                classNamePrefix="select"
              />
            </div>
          )}

          {orgHasContractTypes(userData?.organisation) && (
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Contract Type:
              </label>
              <Select
                value={localFormInputs.assessmentType}
                onChange={(option) => handleLocalInputChange('assessmentType', option)}
                options={assessmentTypeOptions}
                isClearable
                className="w-full"
                classNamePrefix="select"
              />
            </div>
          )}

          {showModuleFilter && (
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Pathway:
              </label>
              <Select
                value={localFormInputs.assessmentModule}
                onChange={(option) => handleLocalInputChange('assessmentModule', option)}
                options={ASSESSMENT_MODULE_OPTIONS}
                isClearable
                placeholder="All pathways..."
                className="w-full"
                classNamePrefix="select"
              />
            </div>
          )}

          {/* Archive-specific filters */}
          {isArchived && (
            <>
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Archive Reason:
                </label>
                <Select
                  value={localFormInputs.archiveReason}
                  onChange={(option) => handleLocalInputChange('archiveReason', option)}
                  options={archiveReasonOptions}
                  isClearable
                  placeholder="Select archive reason..."
                  className="w-full"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Date archived:
                </label>
                <div className="flex items-center gap-2">
                  <DatePicker
                    selectsRange={true}
                    startDate={localFormInputs.archiveDateRange?.startDate}
                    endDate={localFormInputs.archiveDateRange?.endDate}
                    onChange={(update) => {
                      const [start, end] = update;
                      setLocalFormInputs(prev => ({
                        ...prev,
                        archiveDateRange: {
                          startDate: start,
                          endDate: end
                        }
                      }));
                    }}
                    isClearable={true}
                    placeholderText="Select date range..."
                    className="w-[266px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    dateFormat="dd MMM yyyy"
                  />
                </div>
              </div>
            </>
          )}

      <button
        onClick={() => onApplyFilters(localFormInputs)}
        className="font-semibold w-full mt-2 px-4 py-2 bg-violet-700 text-white rounded-lg hover:bg-violet-600 transition-colors duration-200"
      >
        Apply Filters
      </button>
    </div>
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function FilterBadges({ activeFilters, onRemoveFilter, isArchived = false }) {
  const getActiveFilterBadges = () => {
    return Object.entries(activeFilters).filter(([field, value]) => {
      if (value === null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      // Special handling for dateRange object
      if (field === 'dateRange' || field === 'archiveDateRange') {
        return value.startDate && value.endDate;
      }
      return true;
    });
  };
  return (
    <>
      {getActiveFilterBadges().map(([field, value]) => (
        <button
          onClick={() => onRemoveFilter(field)}
          key={field}
          className="text-xs inline-flex items-center px-2 py-0.5 rounded-md font-medium bg-blue-100 text-blue-900 hover:bg-red-200 hover:text-red-800 transition-colors duration-200"
        >
          <p className="mr-1">×</p>
          {field === 'patientName' && `Name contains '${value}'`}
          {field === 'emailAddress' && `Email contains '${value}'`}
          {field === 'referenceNumber' && `Reference contains '${value}'`}
          {field === 'paymentMethod' && `Payment method is '${value.label}'`}
          {field === 'assessmentType' && `Contract type is '${value.label}'`}
          {field === 'assessmentModule' && `Pathway: ${value.label}`}
          {field === 'assignedTo' && `Assigned to: ${value.label}`}
          {field === 'archiveReason' && `Archive reason: ${value.label}`}
          {field === 'dateRange' && value.startDate && value.endDate && 
            `Added ${value.startDate.toLocaleDateString()} - ${value.endDate.toLocaleDateString()}`}
          {field === 'archiveDateRange' && value.startDate && value.endDate && 
            `Archived ${value.startDate.toLocaleDateString()} - ${value.endDate.toLocaleDateString()}`}
        </button>
      ))}
    </>
  );
}

export default function ClinicianDashboard() {
  const { refreshUserData, userData, patientsData } = useContext(GlobalStateContext);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isPatientFormOpen, setIsPatientFormOpen] = useState(false);

  // The current/archived tab is session-persisted so navigating to a patient
  // and back returns the clinician to the same tab they were last working in.
  const [patientSubTab, setPatientSubTab] = useSessionState(DASHBOARD_KEYS.tab, "current");
  
  // Quick-filter chip state — null means no chip is active (default view).
  // Only one chip can be active at a time (mutually exclusive). Session-persisted
  // so an active chip survives navigation back to the dashboard.
  const [activeChip, setActiveChip] = useSessionState(DASHBOARD_KEYS.chip, null);

  // Recently-viewed patients — a session-persisted list of up to 5 patients the
  // clinician opened from the dashboard. Powers the "Recently viewed" dropdown
  // in the Quick Filter row. recordPatientView is passed down to PatientTable
  // and ArchivedPatientsTable so views are recorded at the same navigation
  // points where the caseload analytics event already fires.
  const { recentPatients, recordPatientView } = useRecentlyViewedPatients();

  // Org-gated chip visibility (journey chips + WLC-only "Needs MDT clinician"
  // ops chip). See getVisibleChipKeys for the full rules.
  const visibleChipKeys = useMemo(
    () => getVisibleChipKeys(userData?.organisation),
    [userData?.organisation],
  );

  // Safety: if the active chip gets removed from the visible set (e.g. user's org
  // data loads and reveals the chip is irrelevant), deactivate it gracefully.
  useEffect(() => {
    if (activeChip && !visibleChipKeys.includes(activeChip)) {
      setActiveChip(null);
    }
  }, [activeChip, visibleChipKeys]);

  // availableOrgModules is fetched from getOrgConfig for Org Admin / Support users,
  // who are the primary users of the patient setup form and the most likely to need
  // this filter immediately (before any autism patients exist).  For other roles the
  // fetch is silently skipped and we fall back to the data-driven approach below.
  const [availableOrgModules, setAvailableOrgModules] = useState(['adultAdhd']);

  useEffect(() => {
    const isAdminOrSupport =
      userData?.userType === 'Org. Admin' || userData?.userType === 'Support';
    if (!isAdminOrSupport) return;

    let cancelled = false;
    const fetchOrgModules = async () => {
      try {
        const getOrgConfigFn = httpsCallable(functions, 'getOrgConfig');
        const result = await getOrgConfigFn();
        if (cancelled) return;
        const modules = result?.data?.availableAssessmentModules;
        if (Array.isArray(modules) && modules.length > 0) {
          setAvailableOrgModules(modules);
        }
      } catch (_) {
        // Non-fatal: clinicians can't call this; fall back to data-driven only.
      }
    };
    fetchOrgModules();
    return () => { cancelled = true; };
  }, [userData?.userType]);

  // Assignable clinicians for the "Assigned to" filter. Same org-wide read
  // every Assessment provider is allowed (Firestore: same-org clinician docs).
  // Loaded once per org so Current and Archived popovers share the list.
  const [assignedToOptions, setAssignedToOptions] = useState([]);

  useEffect(() => {
    const organisation = userData?.organisation;
    if (!organisation) return;

    let cancelled = false;
    const loadAssignableClinicians = async () => {
      try {
        const cliniciansQuery = query(
          collection(db, 'clinicians'),
          where('organisation', '==', organisation),
          where('userType', 'in', ['Org. Admin', 'Clinician'])
        );
        const snapshot = await getDocs(cliniciansQuery);
        if (cancelled) return;
        const accounts = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setAssignedToOptions(buildAssignableClinicianSelectOptions(accounts));
      } catch (error) {
        console.error('Failed to load assignable clinicians for dashboard filter:', error);
        if (!cancelled) setAssignedToOptions([]);
      }
    };
    loadAssignableClinicians();
    return () => { cancelled = true; };
  }, [userData?.organisation]);

  // Show the module filter when:
  // 1. The org's config declares >1 module (Org Admin / Support — shows immediately
  //    after the backfill, even before any autism patients are created), OR
  // 2. The loaded patient summaries already contain >1 distinct module (all roles —
  //    kicks in for clinicians once autism patients exist).
  const showModuleFilter = useMemo(() => {
    if (availableOrgModules.length > 1) return true;
    if (!patientsData || patientsData.length === 0) return false;
    const modules = new Set(patientsData.map(p => p.assessmentModule || 'adultAdhd'));
    return modules.size > 1;
  }, [availableOrgModules, patientsData]);

  // Add signature-related state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureExists, setSignatureExists] = useState(false);
  const [signatureChecked, setSignatureChecked] = useState(false);

  // Stable dialog handlers/props to avoid conditional hook usage inside JSX
  const onSignatureDialogOpenChange = useCallback((isOpen) => {
    // Only allow closing if they have a signature
    if (!isOpen && signatureExists) {
      setSignatureDialogOpen(false);
    }
  }, [signatureExists]);

  const initialSignatureConfig = useMemo(() => ({
    customTitle: "Add Your Digital Signature",
    customDescription: "Before you begin, please set up your digital signature which will be used on clinical documents. This is a required step to complete your account setup.",
    isOnboarding: true
  }), []);

  // Initialise dashboard data and UI state:
  // 1) Refresh the signed-in provider's data (and patients via GlobalStateContext)
  // 2) Always clear the loading spinner in a finally block so the UI never gets stuck,
  //    even if the request fails or React Strict Mode re-invokes effects
  // 3) Check whether the user has a saved signature (runs separately so a signature
  //    error will not block the dashboard from rendering)
  useEffect(() => {
    (async () => {
      try {
        // Refresh user data (and patients) via the global context
        await refreshUserData();
      } catch (e) {
        console.error('Dashboard init failed:', e);
      } finally {
        // Ensure the dashboard renders regardless of network/strict-mode behavior
        setIsPageLoading(false);
      }
      try {
        // Independently check if the user needs to add a digital signature
        await checkUserSignature();
      } catch (_) {}
    })();
  }, [refreshUserData]);
  
  // Add function to check if the user has a signature
  const checkUserSignature = async () => {
    try {
      if (!auth.currentUser) return;
      
      const clinicianDoc = await getDoc(doc(db, 'clinicians', auth.currentUser.uid));
      if (clinicianDoc.exists()) {
        const data = clinicianDoc.data();
        
        // Check if signature path exists
        if (data.signaturePath) {
          console.log("User already has a signature");
          setSignatureExists(true);
        } else {
          console.log("User does not have a signature");
          setSignatureExists(false);
          // Show signature dialog if the user doesn't have a signature
          setSignatureDialogOpen(true);
        }
      } else {
        console.log("Clinician document not found");
        setSignatureExists(false);
        // Show signature dialog if the user's document doesn't exist
        setSignatureDialogOpen(true);
      }
      
      setSignatureChecked(true);
    } catch (err) {
      console.error('Error checking signature:', err);
    }
  };

  if (userData) {
    console.log('Logged in user:', {
      organisation: userData.organisation,
      accountType: userData.accountType,
      userType: userData.userType,
      clinicianId: userData.clinicianId,
      isEmailVerified: userData.isEmailVerified,
      isMFASetUp: userData.isMFASetUp
    });
  }

  // Separate filter states for current and archived patients
  const [isCurrentFilterPopoverOpen, setIsCurrentFilterPopoverOpen] = useState(false);
  const [isArchivedFilterPopoverOpen, setIsArchivedFilterPopoverOpen] = useState(false);
  
  // Search/filter values for both tabs are session-persisted (including the
  // date-range Date objects, which the persistence layer serialises). This is
  // the core fix for the "had to type the patient's name 2-3 times" report:
  // the typed filter is no longer wiped when the dashboard remounts.
  const [currentPatientsFilters, setCurrentPatientsFilters] = useSessionState(
    DASHBOARD_KEYS.currentFilters,
    () => ({ ...DEFAULT_CURRENT_FILTERS })
  );

  const [archivedPatientsFilters, setArchivedPatientsFilters] = useSessionState(
    DASHBOARD_KEYS.archivedFilters,
    () => ({ ...DEFAULT_ARCHIVED_FILTERS })
  );

  // Handles chip toggle logic and clears text/select/date filters when a chip
  // is activated. This avoids compound filter states that would confuse ops.
  const handleChipChange = (chipKey) => {
    if (chipKey) {
      // Activating a chip — clear any existing search/filter criteria
      setCurrentPatientsFilters({ ...DEFAULT_CURRENT_FILTERS });
      setIsCurrentFilterPopoverOpen(false);
    }
    setActiveChip(chipKey);
  };

  // Switching tabs preserves the Current tab's quick-filter chip (and its text
  // filters, which were already preserved). Returning to Current therefore
  // restores the exact view the clinician left — consistent with how the
  // dashboard persists its view state across navigation. The chip only affects
  // the Current tab, so leaving it set while on Archived is harmless.
  const handleTabChange = (newTab) => {
    setPatientSubTab(newTab);
  };

  const handleApplyCurrentFilters = (formValues) => {
    setCurrentPatientsFilters(formValues);
    setIsCurrentFilterPopoverOpen(false);
  };

  const handleApplyArchivedFilters = (formValues) => {
    setArchivedPatientsFilters(formValues);
    setIsArchivedFilterPopoverOpen(false);
  };

  const removeCurrentFilter = (field) => {
    setCurrentPatientsFilters(prev => ({
      ...prev,
      [field]: (field === 'paymentMethod' || field === 'assessmentType' || field === 'assessmentModule' || field === 'assignedTo') ? null :
               (field === 'dateRange') ? { startDate: null, endDate: null } : ''
    }));
  };

  const removeArchivedFilter = (field) => {
    setArchivedPatientsFilters(prev => ({
      ...prev,
      [field]: (field === 'paymentMethod' || field === 'assessmentType' || field === 'assessmentModule' || field === 'assignedTo' || field === 'archiveReason') ? null :
               (field === 'dateRange' || field === 'archiveDateRange') ? { startDate: null, endDate: null } : ''
    }));
  };

  const LoadingSpinner = () => (
    <div className="w-full h-24 flex items-center justify-center">
      <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-slate-800 rounded-lg">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="font-semibold">Loading dashboard...</span>
      </div>
    </div>
  );

  const WarningIcon = () => (
    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );

  // NOTE: We intentionally render the dashboard content inline rather than defining
  // an inner component (e.g. `const TabContent = () => ...`) because that pattern
  // creates a new component type on every render.
  //
  // When `refreshUserData()` updates global context, ClinicianDashboard re-renders.
  // If TabContent is a freshly-created function, React treats it as a different
  // component and remounts the subtree, which can reset open dialogs mid-submit.

  // Loading state
  if (isPageLoading || !userData) {
    return (
      <Layout activePage="dashboard">
        <div className="flex-grow p-8">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  // Account type check
  if (userData.accountType !== 'Provider') {
    return (
      <Layout activePage="dashboard">
        <StatusMessage color="yellow" icon={<WarningIcon />}>
          This dashboard is only accessible to Provider accounts.
        </StatusMessage>
      </Layout>
    );
  }

  return (
    <Layout activePage="dashboard">
      <div className="flex-grow p-8">
        <div className="flex-grow">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>

          <PatientSetupForm
            isOpen={isPatientFormOpen}
            onClose={() => setIsPatientFormOpen(false)}
          />

          {/* Patient Tabs: Current vs Archived */}
          <Tabs.Root
            value={patientSubTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <Tabs.List className="flex border-b border-gray-200 mb-6">
              <Tabs.Trigger
                value="current"
                className="px-4 py-2 -mb-px text-sm font-medium text-gray-500 border-b-2 border-transparent hover:text-gray-700 hover:border-gray-300 data-[state=active]:border-violet-900 data-[state=active]:text-violet-900 transition-colors duration-200"
              >
                Current Patients
              </Tabs.Trigger>
              <Tabs.Trigger
                value="archived"
                className="px-4 py-2 -mb-px text-sm font-medium text-gray-500 border-b-2 border-transparent hover:text-gray-700 hover:border-gray-300 data-[state=active]:border-violet-900 data-[state=active]:text-violet-900 transition-colors duration-200"
              >
                Archived Patients
              </Tabs.Trigger>
            </Tabs.List>

            {/* Current Patients Tab Content */}
            <Tabs.Content value="current">
              <div className="flex items-center justify-between mb-4">
                {/* Left side - Heading */}
                <h2 className="text-xl font-semibold text-gray-800">
                  Current Patients
                </h2>

                {/* Right side - Filters and Actions */}
                <div className="flex items-center gap-2">
                  {/* Filter badges — hidden when a chip is active since filters are cleared */}
                  {!activeChip && (
                    <div className="flex items-center gap-1">
                      <FilterBadges
                        activeFilters={currentPatientsFilters}
                        onRemoveFilter={removeCurrentFilter}
                        isArchived={false}
                      />
                    </div>
                  )}

                  {/* Search & Filter button — disabled when a chip filter is active */}
                  <FilterPopoverContent
                    isOpen={isCurrentFilterPopoverOpen}
                    onOpenChange={setIsCurrentFilterPopoverOpen}
                    onApplyFilters={handleApplyCurrentFilters}
                    initialValues={currentPatientsFilters}
                    isArchived={false}
                    disabled={!!activeChip}
                    showModuleFilter={showModuleFilter}
                    assignedToOptions={assignedToOptions}
                  />

                  {/* Only Org. Admin and Support users can add new patients */}
                  {(userData?.userType === 'Org. Admin' || userData?.userType === 'Support') && (
                    <button
                      className="flex text-sm items-center py-2 px-4 rounded-lg shadow-sm text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-300"
                      onClick={() => setIsPatientFormOpen(true)}
                    >
                      <span className="font-semibold">+ Set Up New Patient</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Quick-filter row — chips left, recently-viewed right */}
              <div className="flex items-start justify-between gap-4 mb-3">
                <QuickFilterChips
                  patients={patientsData}
                  activeChip={activeChip}
                  onChipChange={handleChipChange}
                  visibleChipKeys={visibleChipKeys}
                />
                <RecentlyViewedDropdown
                  recentPatients={recentPatients}
                  onRecordPatientView={recordPatientView}
                />
              </div>

              {/* No force-refresh callbacks: archive/delete use optimistic updates that
                 are immediately correct. Forcing a refresh races with the Firestore
                 trigger and can overwrite the optimistic state with stale summary data.
                 The next SWR cycle (30s TTL) confirms with the server. */}
              <PatientTable
                key="current-patients-table"
                activeFilters={currentPatientsFilters}
                activeChip={activeChip}
                onRecordPatientView={recordPatientView}
              />
            </Tabs.Content>

            {/* Archived Patients Tab Content */}
            <Tabs.Content value="archived">
              <div className="flex items-center justify-between mb-4">
                {/* Left side - Heading */}
                <h2 className="text-xl font-semibold text-gray-800">
                  Archived Patients
                </h2>

                {/* Right side - Filters and Actions */}
                <div className="flex items-center gap-2">
                  {/* Filter badges group */}
                  <div className="flex items-center gap-1">
                    <FilterBadges
                      activeFilters={archivedPatientsFilters}
                      onRemoveFilter={removeArchivedFilter}
                      isArchived={true}
                    />
                  </div>

                  {/* Search & Filter button */}
                  <FilterPopoverContent
                    isOpen={isArchivedFilterPopoverOpen}
                    onOpenChange={setIsArchivedFilterPopoverOpen}
                    onApplyFilters={handleApplyArchivedFilters}
                    initialValues={archivedPatientsFilters}
                    isArchived={true}
                    showModuleFilter={showModuleFilter}
                    assignedToOptions={assignedToOptions}
                  />
                </div>
              </div>

              <ArchivedPatientsTable
                key="archived-patients-table"
                activeFilters={archivedPatientsFilters}
                onRecordPatientView={recordPatientView}
              />
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </div>
      
      {/* Signature Dialog - shows on page load if user has no signature */}
      {signatureChecked && (
        <SignatureDialog
          open={signatureDialogOpen}
          onOpenChange={onSignatureDialogOpenChange}
          currentUserName={auth.currentUser?.displayName || ''}
          initialSignature={initialSignatureConfig}
          onSave={async (signatureData) => {
            try {
              // Prepare data for cloud function
              const updates = {};
              
              // Handle signature data
              if (signatureData.data) {
                updates.signature = {
                  data: signatureData.data,
                  type: signatureData.type,
                  name: signatureData.name,
                  signatureType: signatureData.signatureType
                };
                
                if (signatureData.style) {
                  updates.signature.style = signatureData.style;
                  updates.signature.text = signatureData.text;
                }
              }
              
              // Handle metadata
              if (signatureData.signatureMetadata) {
                updates.signatureMetadata = signatureData.signatureMetadata;
              }
              
              // Save to Firebase
              const updateClinicianBranding = httpsCallable(functions, 'updateClinicianBranding');
              const result = await updateClinicianBranding(updates);
              
              // Update signature exists state
              setSignatureExists(true);
              
              // Return the result to the component
              return result;
            } catch (error) {
              console.error("Error saving signature:", error);
              throw error;
            }
          }}
          onDelete={null}
        />
      )}
    </Layout>
  );
}
